import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import PixPayment from "./PixPayment";

export const brl = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const SOURCES = { consumption: "Saldo Consumo", balance: "Saldo disponível" };

const SourcePicker = ({ source, setSource, balances }) => (
  <div className="grid grid-cols-2 gap-2" role="radiogroup">
    {Object.entries(SOURCES).map(([v, l]) => (
      <button type="button" key={v} role="radio" aria-checked={source === v} onClick={() => setSource(v)}
              className={`p-3 rounded-lg border text-left text-sm transition-colors ${source === v ? "gold-border bg-amber-500/5" : "border-border text-muted-foreground"}`}
              data-testid={`purchase-source-${v}`}>
        <div className="font-medium">{l}</div>
        <div className="font-mono text-xs">{brl(balances[v])}</div>
      </button>
    ))}
  </div>
);

const Summary = ({ item, showBalance, balance, label }) => {
  const after = balance - item.price;
  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between gap-3"><span>Item</span><b className="text-right">{item.name}</b></div>
      {item.original_price && <div className="flex justify-between"><span>Preço original</span>
        <span className="font-mono line-through text-muted-foreground">{brl(item.original_price)}</span></div>}
      {showBalance && <div className="flex justify-between"><span>{label}</span>
        <span className="font-mono" data-testid="purchase-balance">{brl(balance)}</span></div>}
      <div className="flex justify-between"><span>Valor com desconto</span>
        <span className="font-mono text-rose-500" data-testid="purchase-price">− {brl(item.price)}</span></div>
      {showBalance && <div className="flex justify-between border-t border-border pt-2"><span>Saldo após a compra</span>
        <span className={`font-mono font-bold ${after >= 0 ? "" : "text-rose-500"}`} data-testid="purchase-after">{brl(after)}</span></div>}
    </div>
  );
};

export const PurchaseDialog = ({ item, mode, balances, buyer = false, onClose, onDone }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("consumption");
  const [pix, setPix] = useState(null);
  if (!item) return null;
  const isPix = mode === "pix";
  const src = buyer ? "balance" : source;
  const balance = balances[src] || 0;
  const enough = isPix || balance >= item.price;
  const close = () => { setPix(null); onClose(); };

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/store/orders", { benefit_id: item.id, method: isPix ? "pix" : src });
      if (isPix) { setPix(data.pix); return; }
      toast.success(`Pedido #${data.code} realizado! Acompanhe em Meus pedidos.`);
      onDone();
      close();
      navigate("/pedidos");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const paid = () => { toast.success("Pix confirmado! Seu pedido foi enviado."); onDone(); close(); navigate("/pedidos"); };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="purchase-dialog">
        <DialogHeader>
          <DialogTitle>{pix ? "Pague o Pix do pedido" : isPix ? "Confirmar pagamento via Pix" : "Pagar com saldo Conecta Mais"}</DialogTitle>
          <DialogDescription>
            {pix ? "O pedido é confirmado automaticamente após o pagamento." : isPix ? "Vamos gerar um QR Code Pix com o valor do produto." : buyer ? "O valor será descontado do seu saldo disponível." : "Escolha de qual saldo descontar."}
          </DialogDescription>
        </DialogHeader>
        {pix ? <PixPayment initial={pix} onPaid={paid} /> : (
          <>
            {!isPix && !buyer && <SourcePicker source={source} setSource={setSource} balances={balances} />}
            <Summary item={item} showBalance={!isPix} balance={balance} label={SOURCES[src]} />
            {!enough && <p className="text-xs text-rose-500" data-testid="purchase-insufficient">Saldo insuficiente — escolha outro saldo ou pague com Pix.</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close} data-testid="purchase-cancel-btn">Cancelar</Button>
              <Button disabled={!enough || busy} onClick={buy} className="gold-btn" data-testid="purchase-confirm-btn">
                {busy ? "Processando..." : isPix ? "Confirmar e gerar Pix" : "Confirmar pagamento"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
